/** The Authentik entitlement that marks a platform operator; emitted in the token's `roles` claim. */
export const PLATFORM_OPERATOR_ROLE = 'platform-operator'

/** Decode a JWT's payload without verifying it. Null for anything that is not a three-part JWT. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  if (parts.length !== 3 || !parts[1]) return null
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = decodeURIComponent(
      Array.from(atob(padded), (ch) => '%' + ch.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    )
    const payload: unknown = JSON.parse(json)
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null
  } catch {
    return null
  }
}

function hasRole(claims: Record<string, unknown> | null | undefined): boolean {
  const roles = claims?.['roles']
  return Array.isArray(roles) && roles.includes(PLATFORM_OPERATOR_ROLE)
}

/**
 * Whether to SHOW the operator console entry point, from the signed-in user's tokens.
 *
 * Reads the access token first - it is what the worker validates, so it is the token whose roles
 * matter - and requires it to have been issued to this portal's client (roles are per-application
 * Authentik entitlements). Falls back to the id_token's profile claims when the access token is not
 * a decodable JWT.
 *
 * DISPLAY ONLY. Nothing here is verified, and nothing needs to be: the worker decides admission on
 * every /api/platform call (role + client + operator allowlist), so a forged claim buys a button
 * that leads to a 403.
 */
export function isPlatformOperatorToken(
  accessToken: string | undefined,
  profile: Record<string, unknown> | undefined,
  clientId: string,
): boolean {
  const access = accessToken ? decodeJwtPayload(accessToken) : null
  if (access) {
    return hasRole(access) && access['azp'] === clientId
  }
  return hasRole(profile)
}
